import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router';
import { Capacitor } from '@capacitor/core';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { persistor, RootState } from 'core/store';
import { clearStorage, removeAuthTokenFromStorage } from 'core/util';
import { WARRANTY_BASE } from 'core/constants';
import { fetchLinkToken } from 'core/hooks/useCustomTokenLink';
import { openInAppBrowserLink } from 'core/util/helpers';
import {
  buildWarrantyUrl,
  shouldRedirectToWarranty
} from 'core/util/warrantyAccess';
import { logout } from 'features/shared/login/LoginSlice';
import { resetAppToInitialState } from 'features/shared/login/LoginActions';

// Past this, give up on the signed-in handoff and send the user to the builder
// page to sign in there, rather than leaving them on the Checkpoint spinner.
const LINK_TOKEN_TIMEOUT_MS = 10000;

/** Whether the signed-in user is warranty-only and must leave HomeKeep. */
export const selectIsWarrantyOnly = (state: RootState): boolean => {
  const { loggedIn, accessToken } = state.login;
  const { homes, globalAccountSettings } = state.home;
  return (
    loggedIn &&
    !!accessToken &&
    !!globalAccountSettings &&
    shouldRedirectToWarranty({
      homes,
      isAdmin: globalAccountSettings.admin,
      warrantySlug: globalAccountSettings.avid_warranty_url
    })
  );
};

// Select a primitive here, never whole slices. Subscribers re-render
// on every slice change, and a re-render of Routes remounts every PrivateRoute
// (RouteList is defined inside Routes), each forcing a Firebase token refresh
// until Firebase returns auth/quota-exceeded and logs the user out.
export const useIsWarrantyOnly = (): boolean =>
  useSelector(selectIsWarrantyOnly);

/**
 * Hands warranty-only users off to their builder's Avid Warranty site, signed in
 * via a link token, then clears the HomeKeep session. Mounted once via
 * <WarrantyOnlyRedirect /> in App (outside Routes) so it covers /checkpoint
 * (login, password reset) and restored sessions alike.
 */
export const useWarrantyOnlyRedirect = (): void => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isWarrantyOnly = useIsWarrantyOnly();
  const store = useStore<RootState>();
  const inFlight = useRef(false);

  useEffect(() => {
    if (!isWarrantyOnly || inFlight.current) {
      return;
    }
    inFlight.current = true;
    // Read from the store at handoff time rather than subscribing to it.
    const { login, home } = store.getState();
    const accessToken = login.accessToken!;
    const slug = home.globalAccountSettings!.avid_warranty_url!;

    // Fully sign out and wipe every stored copy of the session BEFORE any
    // state change React can react to. Dispatching logout() first re-renders
    // PrivateRoute -> /login, and Login restores the session from the
    // not-yet-removed auth_token and bounces straight back to /checkpoint.
    // Persistence stays paused on failure: the web path never dispatches
    // logout(), so resuming would write the signed-in state back to storage.
    const tearDownSession = async () => {
      persistor.pause();
      // pause() doesn't drain writes already queued on redux-persist's timer;
      // one landing after purge() would restore the persisted session.
      await persistor
        .flush()
        .catch((err) => console.warn('Warranty handoff: flush failed', err));
      const steps: Record<string, () => Promise<unknown>> = {
        firebaseSignOut: () => firebase.auth().signOut(),
        pluginSignOut: () => FirebaseAuthentication.signOut(),
        removeAuthToken: removeAuthTokenFromStorage,
        clearStorage,
        purgePersistedState: () => persistor.purge()
      };
      // Storage steps are what Login restores a session from; retry them once.
      const retryable = [
        'removeAuthToken',
        'clearStorage',
        'purgePersistedState'
      ];
      const run = async (names: string[]) => {
        const results = await Promise.allSettled(names.map((n) => steps[n]()));
        return names.filter((name, i) => {
          const result = results[i];
          if (result.status === 'rejected') {
            console.warn(`Warranty handoff: ${name} failed`, result.reason);
            return true;
          }
          return false;
        });
      };
      const failed = await run(Object.keys(steps));
      const stillFailed = await run(
        failed.filter((n) => retryable.includes(n))
      );
      const unresolved = [
        ...failed.filter((n) => !retryable.includes(n)),
        ...stillFailed
      ];
      if (unresolved.length) {
        console.error('Warranty handoff cleanup incomplete:', unresolved);
      }
    };

    // Native only: the page stays loaded, so in-memory state must be reset too.
    // Storage is already empty, so nothing can restore the session.
    const resetInMemorySession = () => {
      dispatch(logout());
      dispatch(resetAppToInitialState());
      persistor.persist();
      history.replace('/login');
    };

    const handOff = async () => {
      let linkToken: string | null = null;
      const controller = new AbortController();
      const deadline = setTimeout(
        () => controller.abort(),
        LINK_TOKEN_TIMEOUT_MS
      );
      try {
        linkToken = await fetchLinkToken(accessToken, controller.signal);
      } catch (err) {
        // Includes the deadline abort. Fall back to the builder page; the user
        // signs in there instead.
        console.error('Warranty link token failed:', err);
      } finally {
        clearTimeout(deadline);
      }
      const url = buildWarrantyUrl(WARRANTY_BASE, slug, linkToken);

      await tearDownSession();

      if (Capacitor.isNativePlatform()) {
        // Navigating the webview itself would strand the user outside the app.
        openInAppBrowserLink(url, store.getState().platform.platformType);
        resetInMemorySession();
        inFlight.current = false;
      } else {
        // The page unloads, so in-memory state doesn't matter; skipping the
        // logout dispatch avoids the PrivateRoute/Login re-render entirely.
        window.location.replace(url);
      }
    };

    handOff().catch(async (err) => {
      // Never strand the user on the Checkpoint spinner: drop the session so
      // isWarrantyOnly clears, and return to /login where signing in retries.
      console.error('Warranty handoff failed:', err);
      await tearDownSession();
      resetInMemorySession();
      inFlight.current = false;
    });
  }, [isWarrantyOnly]);
};
